import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ArrowRight,
  MessageCircle,
  History,
  Sparkles,
  Vote,
  Factory,
  Crown,
} from "lucide-react";

export default function About() {
  const { t } = useTranslation();
  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="about-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-[10px] md:text-xs font-mono tracking-[0.2em] text-[var(--t-blue-glow)] uppercase hover:text-[var(--t-cyan)] mb-4"
        data-testid="about-back-home"
      >
        <ChevronLeft className="w-3 h-3" /> {t("nav.home")}
      </Link>

      <section
        className="panel corner-frame p-6 md:p-10 mb-8 md:mb-12 relative overflow-hidden"
        data-testid="about-hero"
      >
        <span
          aria-hidden
          className="absolute -inset-x-2 top-0 h-px bg-gradient-to-r from-transparent via-[var(--t-cyan)] to-transparent opacity-60"
        />
        <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">
          {t("about.eyebrow")}
        </span>
        <h1 className="font-display text-3xl md:text-5xl lg:text-6xl mt-2 leading-[1.05]">
          <span className="text-[var(--t-text)]">{t("about.title")}</span>
        </h1>
        <p className="text-base md:text-lg text-[var(--t-text-dim)] mt-5 md:mt-6 max-w-3xl leading-relaxed">
          {t("about.subtitle")}
        </p>
        <div className="mt-6 md:mt-8 flex flex-wrap gap-3">
          <a
            href="https://territoria.world"
            target="_blank"
            rel="noreferrer"
            className="btn-primary !justify-center"
            data-testid="about-cta-enter-world"
          >
            {t("about.joinCta")} <ArrowRight className="w-4 h-4" />
          </a>
          <Link to="/discord" className="btn-ghost !justify-center" data-testid="about-cta-discord">
            <MessageCircle className="w-4 h-4" /> {t("home.joinDiscord")}
          </Link>
        </div>
      </section>

      <Section eyebrow={t("about.eyebrow")} title={t("about.missionTitle")}>
        <p className="text-base text-[var(--t-text-dim)] leading-relaxed max-w-3xl">{t("about.missionBlurb")}</p>
      </Section>

      <Section eyebrow={t("about.eyebrow")} title={t("about.visionTitle")}>
        <p className="text-base text-[var(--t-text-dim)] leading-relaxed max-w-3xl">{t("about.visionBlurb")}</p>
      </Section>

      <Section eyebrow={t("about.eyebrow")} title={t("about.pillarsTitle")}>
        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          <Pillar icon={Vote}    title={t("about.pillarPlayerDrivenTitle")}  body={t("about.pillarPlayerDrivenBody")} />
          <Pillar icon={Factory} title={t("about.pillarRealEconomyTitle")}   body={t("about.pillarRealEconomyBody")} />
          <Pillar icon={History} title={t("about.pillarLivingHistoryTitle")} body={t("about.pillarLivingHistoryBody")} />
          <Pillar icon={Crown}   title={t("about.pillarMmoScaleTitle")}      body={t("about.pillarMmoScaleBody")} />
        </div>
      </Section>

      <Section eyebrow={t("about.eyebrow")} title={t("about.valuesTitle")}>
        <ul className="grid sm:grid-cols-2 gap-3 max-w-3xl">
          {["about.value1", "about.value2", "about.value3", "about.value4"].map((k) => (
            <li key={k} className="flex items-start gap-2.5 text-base text-[var(--t-text-dim)]">
              <Sparkles className="w-4 h-4 text-[var(--t-blue-glow)] flex-shrink-0 mt-0.5" /> {t(k)}
            </li>
          ))}
        </ul>
      </Section>

      <section className="panel panel-glow corner-frame p-6 md:p-10 mt-10" data-testid="about-join">
        <h2 className="font-display text-2xl md:text-4xl text-[var(--t-text)] uppercase">{t("about.joinTitle")}</h2>
        <p className="mt-4 text-base text-[var(--t-text-dim)] max-w-2xl">{t("about.joinBlurb")}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="https://territoria.world" target="_blank" rel="noreferrer" className="btn-primary !justify-center">
            {t("about.joinCta")} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
}

function Section({ eyebrow, title, children }) {
  return (
    <section className="mb-10">
      <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">{eyebrow}</span>
      <h2 className="font-display text-2xl md:text-3xl mt-1 mb-4 text-[var(--t-text)] uppercase">{title}</h2>
      {children}
    </section>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div className="panel corner-frame p-5 md:p-6">
      <Icon className="w-7 h-7 text-[var(--t-blue-glow)] mb-3" />
      <h3 className="font-display text-lg md:text-xl tracking-[0.06em] text-[var(--t-text)] uppercase mb-2">{title}</h3>
      <p className="text-sm md:text-base text-[var(--t-text-dim)] leading-relaxed">{body}</p>
    </div>
  );
}
